import { useState, useEffect, useRef } from "react";
import "../landing.css";

// ── SVG Icon helper ───────────────────────────────────────────────────────────
const PATHS = {
  dollar:    "M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  home:      "M3 12L12 3l9 9M5 10v10h5v-5h4v5h5V10",
  users:     "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  tool:      "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z",
  msg:       "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  doc:       "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8",
  receipt:   "M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  clipboard: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12l2 2 4-4",
  refresh:   "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
  alert:     "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01",
  trend:     "M22 7L13.5 15.5 8.5 10.5 2 17M16 7h6v6",
  pin:       "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0z",
  equity:    "M3 3v18h18M18 9l-5 5-4-4-4 4",
  shield:    "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  send:      "M22 2L11 13M22 2L15 22 11 13 2 9l20-7z",
  building:  "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10",
};
function SvgIc({ n, s = 22, c = "currentColor" }) {
  const d = PATHS[n];
  if (!d) return null;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c}
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d={d}/>
    </svg>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────
const FEATURES = [
  { ic:"dollar",   name:"Rent Collection",       desc:"Accept ACH and card. Track paid, partial, and late. Late fee calculator with Texas §92.019 compliance.", tag:"all",  accent:"#22c55e" },
  { ic:"building", name:"Property Tracker",       desc:"Full equity dashboard, capital improvements log, maintenance schedule, insurance and mortgage per property.", tag:"pro",  accent:"#d4a017" },
  { ic:"users",    name:"Tenant Management",      desc:"Full profiles, contact info, lease history, balance tracking and status management across all properties.", tag:"all",  accent:"#58a6ff" },
  { ic:"tool",     name:"Maintenance Requests",   desc:"Tenants submit through their portal. You manage priority, status and resolution with a full audit trail.", tag:"all",  accent:"#f97316" },
  { ic:"msg",      name:"In-App Messaging",       desc:"Direct messaging with each tenant. Broadcast to all or selected tenants. Full message history logged.", tag:"all",  accent:"#a78bfa" },
  { ic:"doc",      name:"Document Vault",         desc:"Store leases, checklists, insurance certs, and more. Tenants access their own documents from their portal.", tag:"all",  accent:"#22c55e" },
  { ic:"receipt",  name:"Rent Receipts",          desc:"Generate official receipts for every payment. Tenants download from their portal. Professional output.", tag:"all",  accent:"#58a6ff" },
  { ic:"clipboard",name:"Move-In/Out Checklists", desc:"Room-by-room condition checklists. Protect against deposit disputes with timestamped documentation.", tag:"all",  accent:"#f97316" },
  { ic:"refresh",  name:"Lease Renewal",          desc:"Urgency-sorted renewal pipeline. Send personalized renewal offers. Never miss an expiring lease.", tag:"pro",  accent:"#d4a017" },
  { ic:"alert",    name:"Notice to Vacate",       desc:"Formal notice creates a timestamped legal record. PDF export for your files. Status tracking included.", tag:"pro",  accent:"#f85149" },
  { ic:"trend",    name:"Expense Tracker",        desc:"Log every property expense with category filters and missing receipt warnings for tax season.", tag:"all",  accent:"#22c55e" },
  { ic:"pin",      name:"DFW Vendor Network",     desc:"Curated directory of trusted DFW contractors — plumbers, HVAC, electricians, attorneys. Exclusive deals.", tag:"all",  accent:"#a78bfa" },
];

const INVESTOR_FEATS = [
  { ic:"equity",   accent:"#d4a017", title:"Property Equity Tracker",    desc:"See your equity, appreciation %, and mortgage balance for every property. No spreadsheets. Updated in real time." },
  { ic:"building", accent:"#d4a017", title:"Portfolio Value Dashboard",  desc:"Your entire portfolio's value, total equity, and monthly obligations in one clean view. Know your net worth at a glance." },
  { ic:"tool",     accent:"#d4a017", title:"Capital Improvements Log",   desc:"Track every renovation, repair, and upgrade by property and year. Essential for tax deductions and true cost basis." },
  { ic:"trend",    accent:"#d4a017", title:"Vacancy Tracker",            desc:"Know exactly how much every vacant day costs you. Track status, target move-in dates, and accumulated income loss." },
];

const VS_ROWS = [
  { feat:"Monthly Price",             rl:"$29–$49 ✅", tt:"Free* (hidden fees)", dl:"$69+" },
  { feat:"Property Equity Tracker",   rl:"✅", tt:"❌", dl:"❌" },
  { feat:"Portfolio Value Dashboard",  rl:"✅", tt:"❌", dl:"❌" },
  { feat:"Capital Improvements Log",   rl:"✅", tt:"❌", dl:"❌" },
  { feat:"Rent Collection",            rl:"✅", tt:"✅", dl:"✅" },
  { feat:"In-App Messaging",           rl:"✅", tt:"❌", dl:"✅" },
  { feat:"Late Fee Calculator",        rl:"✅", tt:"❌", dl:"❌" },
  { feat:"Vacancy Tracker",            rl:"✅", tt:"❌", dl:"❌" },
  { feat:"Move-In/Out Checklists",     rl:"✅", tt:"❌", dl:"✅" },
  { feat:"Hidden Tenant Fees",         rl:"Never", tt:"Yes — ACH & card fees", dl:"Varies" },
  { feat:"Modern Mobile Design",       rl:"Best in class", tt:"Dated", dl:"Decent" },
];

const STARTERS   = ["Landlord & Tenant Portals","Rent Collection (ACH + Card)","Tenant Management","Lease Tracking","Maintenance Requests","In-App Messaging","Document Vault","Late Fee Calculator","Rent Receipts","Move-In/Out Checklists","Expense Tracker","DFW Vendor Network"];
const PROS       = ["Everything in Starter","Property Equity Tracker","Portfolio Value Dashboard","Capital Improvements Log","Vacancy Tracker","Lease Renewal Automation","Broadcast Messaging","Notice to Vacate","Unlimited Documents","Owner Portal","Email + Live Chat Support"];
const PORTFOLIOS = ["Everything in Pro","Unlimited Units","Multiple Team Members","Custom Branding","API Access","QuickBooks Sync","Dedicated Account Manager","Priority Support"];

const TESTIMONIALS = [
  { av:"DW", name:"D. Williams", role:"6 units · Lewisville, TX", text:"Finally a property management app that thinks like an investor. Seeing my equity and portfolio value right next to rent collection is exactly what I needed. Nothing else does this." },
  { av:"MJ", name:"M. Johnson",  role:"12 units · Fort Worth, TX", text:"I was paying $69/mo for DoorLoop and it showed me nothing about my equity. Switched to RentLord Pro at $49 and got way more features. The vacancy tracker alone saves me money every month." },
  { av:"TR", name:"T. Robinson", role:"4 units · Dallas, TX", text:"The tenant portal is clean and my tenants actually use it. Maintenance requests come through the app instead of text messages. Game changer for a small landlord." },
];

const TRUST_ITEMS = [
  "🔒 Bank-level encryption", "✓ Texas §92.019 compliant", "⚡ Real-time updates",
  "📱 Mobile-first design", "✓ No hidden tenant fees", "🏆 DFW's #1 landlord platform",
  "✓ 14-day free trial", "⚡ Setup in under 5 minutes", "🔒 SOC2 in progress",
  "✓ Cancel anytime", "📱 iOS & Android PWA", "✓ No per-unit pricing",
];

const STATS = [
  { end: 49,  suffix: "",  prefix: "$", label: "Per month for Pro — vs $69 at DoorLoop" },
  { end: 17,  suffix: "+", prefix: "",  label: "Landlord management modules" },
  { end: 2,   suffix: "",  prefix: "",  label: "Portals — Landlord & Tenant included" },
  { end: 0,   suffix: "",  prefix: "",  label: "Hidden tenant fees — ever, guaranteed" },
];

// ── Animated counter hook ─────────────────────────────────────────────────────
function useCountUp(end, duration = 1400, started = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    let start = null;
    const step = ts => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(ease * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, end, duration]);
  return val;
}

// ── Scroll-reveal hook ────────────────────────────────────────────────────────
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Stat counter component ────────────────────────────────────────────────────
function StatCard({ stat, started }) {
  const val = useCountUp(stat.end, 1400, started);
  return (
    <div className="lp-stat">
      <div className="lp-stat-num">{stat.prefix}{val}{stat.suffix}</div>
      <div className="lp-stat-lbl">{stat.label}</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LandingPage({ onEnterApp, onShowTerms, onShowPrivacy }) {
  const [scrolled,  setScrolled]  = useState(false);
  const [mobOpen,   setMobOpen]   = useState(false);
  const [statsRef,  statsVisible] = useReveal(0.3);
  const [featRef,   featVisible]  = useReveal(0.1);
  const [invRef,    invVisible]   = useReveal(0.1);
  const [testRef,   testVisible]  = useReveal(0.1);
  const [pricRef,   pricVisible]  = useReveal(0.1);

  useEffect(() => {
    document.documentElement.classList.add("landing-open");
    return () => document.documentElement.classList.remove("landing-open");
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobOpen]);

  const scrollTo = id => {
    setMobOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const enter = () => { setMobOpen(false); onEnterApp(); };

  return (
    <div className="lp-wrap">

      {/* ── MOBILE MENU ─────────────────────────────────────────────────── */}
      <div className={`lp-mob-menu${mobOpen ? " open" : ""}`}>
        <div className="lp-mob-menu-hd">
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 900, color: "#fff" }}>
            Rent<span style={{ color: "#22c55e" }}>Lord</span>
          </div>
          <button className="lp-mob-close" onClick={() => setMobOpen(false)}>×</button>
        </div>
        <div className="lp-mob-links">
          {[["features","Features"],["compare","Compare"],["pricing","Pricing"],["reviews","Reviews"]].map(([id,lbl]) => (
            <span key={id} className="lp-mob-link" onClick={() => scrollTo(id)}>{lbl}</span>
          ))}
        </div>
        <div className="lp-mob-actions">
          <button className="lp-btn-full lp-btn-full-ghost" onClick={enter}>Sign In</button>
          <button className="lp-btn-full lp-btn-full-primary" onClick={enter}>Get Started Free →</button>
        </div>
      </div>

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
        <div className="lp-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <svg width="26" height="26" viewBox="0 0 64 64" fill="none">
            <path d="M10 48 L16 24 L28 36 L32 16 L36 36 L48 24 L54 48 Z" fill="#22c55e"/>
            <rect x="10" y="50" width="44" height="6" rx="3" fill="#22c55e"/>
            <circle cx="32" cy="13" r="4" fill="#86efac"/>
          </svg>
          <span className="lp-nav-name">Rent<span>Lord</span></span>
        </div>
        <div className="lp-nav-links">
          {[["features","Features"],["compare","Compare"],["pricing","Pricing"],["reviews","Reviews"]].map(([id,lbl]) => (
            <span key={id} onClick={() => scrollTo(id)}>{lbl}</span>
          ))}
        </div>
        <div className="lp-nav-cta">
          <button className="lp-btn-ghost" onClick={enter}>Sign In</button>
          <button className="lp-btn-primary" onClick={enter}>Get Started Free</button>
        </div>
        <button className="lp-nav-mob-btn" onClick={() => setMobOpen(true)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <div className="lp-hero">
        <div className="lp-hero-grid"/>
        <div className="lp-hero-glow"/>
        <div className="lp-hero-glow lp-hero-glow-gold"/>
        <div className="lp-hero-inner">
          <div className="lp-hero-left">
            <div className="lp-badge lp-fade-1">
              <span className="lp-badge-dot"/>
              DFW's property platform for real investors
            </div>
            <h1 className="lp-h1 lp-fade-2">
              Manage Properties.<br/>
              <em>Build Wealth.</em>
            </h1>
            <p className="lp-hero-sub lp-fade-3">
              The only platform that tracks your equity, appreciation, and portfolio value alongside rent collection, maintenance, and tenant management. Built for DFW landlords who think like investors.
            </p>
            <div className="lp-hero-actions lp-fade-4">
              <button className="lp-btn-hero" onClick={enter}>Start Free Trial →</button>
              <button className="lp-btn-outline" onClick={() => scrollTo("features")}>See All Features</button>
            </div>
            <div className="lp-proof-row lp-fade-5">
              {["No credit card required","14-day free trial","Cancel anytime","No hidden fees"].map(p => (
                <div key={p} className="lp-proof-item"><span className="lp-proof-check">✓</span>{p}</div>
              ))}
            </div>
          </div>
          <div className="lp-hero-right lp-fade-right">
            <div className="lp-dash-frame">
              <div className="lp-dash-bar">
                <div className="lp-dash-dots"><span/><span/><span/></div>
                <div className="lp-dash-url">rentlordapp.com/dashboard</div>
              </div>
              <div className="lp-dash-body">
                <div className="lp-dash-sidebar">
                  {["Dashboard","Tenants","Leases","Maintenance","Financials","Messages"].map((item, i) => (
                    <div key={item} className={`lp-dash-nav-item${i === 0 ? " active" : ""}`}>{item}</div>
                  ))}
                </div>
                <div className="lp-dash-content">
                  <div className="lp-dash-title">Dashboard</div>
                  <div className="lp-dash-stats">
                    {[{lbl:"Rent Due",val:"$5,850",c:"#f85149"},{lbl:"Portfolio",val:"$1.1M",c:"#d4a017"},{lbl:"Equity",val:"$402K",c:"#22c55e"},{lbl:"Units",val:"7",c:"#58a6ff"}].map(s => (
                      <div key={s.lbl} className="lp-dash-stat-card">
                        <div className="lp-dash-stat-bar" style={{ background: s.c }}/>
                        <div className="lp-dash-stat-lbl">{s.lbl}</div>
                        <div className="lp-dash-stat-val" style={{ color: s.c }}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                  <div className="lp-dash-section-lbl">Tenants</div>
                  {[{n:"Marcus Johnson",u:"Unit A",st:"Current",c:"#22c55e"},{n:"Priya Patel",u:"Unit 2B",st:"Late",c:"#f85149"},{n:"Derek Williams",u:"Unit 101",st:"Current",c:"#22c55e"}].map(t => (
                    <div key={t.n} className="lp-dash-tenant-row">
                      <div className="lp-dash-tenant-av">{t.n.split(" ").map(w=>w[0]).join("")}</div>
                      <div className="lp-dash-tenant-info">
                        <div className="lp-dash-tenant-name">{t.n}</div>
                        <div className="lp-dash-tenant-unit">{t.u}</div>
                      </div>
                      <div className="lp-dash-tenant-badge" style={{ color: t.c, borderColor: t.c + "40", background: t.c + "15" }}>{t.st}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── TRUST MARQUEE ───────────────────────────────────────────────── */}
      <div className="lp-marquee-wrap">
        <div className="lp-marquee-track">
          {[...TRUST_ITEMS, ...TRUST_ITEMS].map((item, i) => (
            <span key={i} className="lp-marquee-item">{item}</span>
          ))}
        </div>
      </div>

      {/* ── STATS ───────────────────────────────────────────────────────── */}
      <div className="lp-stats-band" ref={statsRef}>
        <div className="lp-stats-grid">
          {STATS.map((s, i) => <StatCard key={i} stat={s} started={statsVisible}/>)}
        </div>
      </div>

      {/* ── INVESTOR SECTION ────────────────────────────────────────────── */}
      <div id="features" className="lp-section" ref={invRef}>
        <div className="lp-inner">
          <div className="lp-sec-label lp-gold-label">Built for Investors, Not Just Landlords</div>
          <h2 className="lp-sec-title">
            The only platform that treats you like the<br/>
            <em className="lp-gold-em">real estate investor you are.</em>
          </h2>
          <p className="lp-sec-sub">Every other tool tracks rent. We track equity, appreciation, vacancies, and portfolio growth — because that's what actually builds wealth.</p>
          <div className="lp-inv-grid">
            <div>
              {INVESTOR_FEATS.map((f, i) => (
                <div key={i} className={`lp-inv-feat${invVisible ? " revealed" : ""}`} style={{ transitionDelay: `${i * 100}ms` }}>
                  <div className="lp-inv-ico-wrap lp-gold-ico">
                    <SvgIc n={f.ic} s={20} c="#d4a017"/>
                  </div>
                  <div>
                    <div className="lp-inv-title">{f.title}</div>
                    <div className="lp-inv-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="lp-float">
              <div className="lp-mock">
                <div className="lp-mock-hd">
                  <div className="lp-mock-title">Portfolio Overview</div>
                  <div className="lp-mock-badge">Pro</div>
                </div>
                <div className="lp-mock-stats">
                  {[{lbl:"Portfolio Value",val:"$1.1M",sub:"+18.4% appreciation"},{lbl:"Total Equity",val:"$402K",sub:"36% of portfolio"}].map((st,i) => (
                    <div key={i} className="lp-mock-stat">
                      <div className="lp-mock-stat-bar lp-gold-bar"/>
                      <div className="lp-mock-stat-lbl">{st.lbl}</div>
                      <div className="lp-mock-stat-val lp-gold-val">{st.val}</div>
                      <div className="lp-mock-stat-sub">{st.sub}</div>
                    </div>
                  ))}
                </div>
                <div className="lp-mock-eq-lbl"><span>Mortgage: $702K</span><span>Equity: $402K (36%)</span></div>
                <div className="lp-mock-eq-bar"><div className="lp-mock-eq-fill lp-gold-fill"/></div>
                <div className="lp-mock-prop-lbl">Properties</div>
                {[{name:"Elm Ridge Apts",val:"$498K"},{name:"Maple Court",val:"$267K"},{name:"Canton Flats",val:"$334K"}].map((p,i) => (
                  <div key={i} className="lp-mock-prop">
                    <div className="lp-mock-prop-dot" style={{ background: ["#d4a017","#e8c040","#f5d76a"][i] }}/>
                    <div className="lp-mock-prop-name">{p.name}</div>
                    <div className="lp-mock-prop-val lp-gold-val">{p.val}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── FEATURES GRID ───────────────────────────────────────────────── */}
      <div className="lp-section-dark" ref={featRef}>
        <div className="lp-inner">
          <div className="lp-sec-label">Everything You Need</div>
          <h2 className="lp-sec-title">17 modules. <em>One platform.</em></h2>
          <div className="lp-feat-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className={`lp-feat-card${featVisible ? " revealed" : ""}`} style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <div className="lp-feat-card-top" style={{ background: f.accent }}/>
                <div className="lp-feat-ico-wrap" style={{ background: f.accent + "18", border: `1px solid ${f.accent}30` }}>
                  <SvgIc n={f.ic} s={20} c={f.accent}/>
                </div>
                <div className="lp-feat-name">{f.name}</div>
                <div className="lp-feat-desc">{f.desc}</div>
                <span className={f.tag === "pro" ? "lp-tag-pro" : "lp-tag-all"}>{f.tag === "pro" ? "Pro" : "All Plans"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── COMPARISON TABLE ────────────────────────────────────────────── */}
      <div id="compare" className="lp-section">
        <div className="lp-inner">
          <div className="lp-sec-label">Honest Comparison</div>
          <h2 className="lp-sec-title">How we stack up<br/><em>against the competition.</em></h2>
          <p className="lp-sec-sub">Cheaper than DoorLoop, more investor-focused than TurboTenant, more modern than everyone else.</p>
          <div className="lp-vs-wrap">
            <div className="lp-vs-hd">
              <div className="lp-vs-hd-cell lp-vs-feat-col">Feature</div>
              <div className="lp-vs-hd-rl">
                <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}>
                  <svg width="14" height="14" viewBox="0 0 64 64" fill="none"><path d="M10 48 L16 24 L28 36 L32 16 L36 36 L48 24 L54 48 Z" fill="#22c55e"/></svg>
                  RentLord
                </div>
              </div>
              <div className="lp-vs-hd-cell">TurboTenant</div>
              <div className="lp-vs-hd-cell">DoorLoop</div>
            </div>
            {VS_ROWS.map((row, i) => (
              <div key={i} className="lp-vs-row">
                <div className="lp-vs-cell-feat lp-vs-feat-col">{row.feat}</div>
                <div className="lp-vs-cell-rl">{row.rl}</div>
                <div className="lp-vs-cell">{row.tt}</div>
                <div className="lp-vs-cell">{row.dl}</div>
              </div>
            ))}
          </div>
          <div className="lp-vs-note">*TurboTenant charges tenants $2/ACH and 3.49% on card — on a $1,500 rent that's $52–$627/year paid by your tenants.</div>
        </div>
      </div>

      {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <div id="pricing" className="lp-section-dark" ref={pricRef}>
        <div className="lp-inner">
          <div className="lp-sec-label">Simple Pricing</div>
          <h2 className="lp-sec-title">No hidden fees.<br/><em>No per-unit gotchas.</em></h2>
          <p className="lp-sec-sub">AppFolio charges $280/mo minimum. Buildium $58–$340. We charge $49. And we're the only one built for investors.</p>
          <div className="lp-pricing-grid">
            {[
              { tier:"Starter", price:"$29", units:"Up to 5 units", feats:STARTERS, featured:false, cta:"Start Free Trial" },
              { tier:"Pro",     price:"$49", units:"Up to 20 units", feats:PROS,    featured:true,  cta:"Start Free Trial →" },
              { tier:"Portfolio",price:"$99",units:"Unlimited units", feats:PORTFOLIOS, featured:false, cta:"Contact Us" },
            ].map((p, i) => (
              <div key={i} className={`lp-price-card${p.featured ? " featured" : ""}${pricVisible ? " revealed" : ""}`} style={{ transitionDelay: `${i * 100}ms` }}>
                {p.featured && <div className="lp-price-badge">Most Popular</div>}
                <div className="lp-price-tier">{p.tier}</div>
                <div className="lp-price-amount">
                  <div className="lp-price-num">{p.price}</div>
                  <div className="lp-price-per">/mo</div>
                </div>
                <div className="lp-price-units">{p.units}</div>
                <div className="lp-price-div"/>
                {p.feats.map((f, j) => (
                  <div key={j} className="lp-price-feat">
                    <span className="lp-price-chk">✓</span>{f}
                  </div>
                ))}
                <button className={`lp-price-btn ${p.featured ? "lp-price-btn-featured" : "lp-price-btn-default"}`} onClick={enter}>
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
          <div className="lp-price-note">All plans include a 14-day free trial · No credit card required · Cancel anytime</div>
        </div>
      </div>

      {/* ── TESTIMONIALS ────────────────────────────────────────────────── */}
      <div id="reviews" className="lp-section" ref={testRef}>
        <div className="lp-inner">
          <div className="lp-sec-label">Early Access Reviews</div>
          <h2 className="lp-sec-title">What DFW landlords<br/><em>are saying.</em></h2>
          <div className="lp-testi-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`lp-testi-card${testVisible ? " revealed" : ""}`} style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="lp-testi-stars">★★★★★</div>
                <div className="lp-testi-text">"{t.text}"</div>
                <div className="lp-testi-author">
                  <div className="lp-testi-av">{t.av}</div>
                  <div>
                    <div className="lp-testi-name">{t.name}</div>
                    <div className="lp-testi-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <div className="lp-cta">
        <div className="lp-cta-glow"/><div className="lp-cta-grid"/>
        <div className="lp-cta-inner">
          <div className="lp-sec-label" style={{ textAlign:"center" }}>Get Started Today</div>
          <h2 className="lp-cta-title">Stop collecting rent.<br/>Start building <em>wealth.</em></h2>
          <p className="lp-cta-sub">Join DFW landlords managing their properties like investors. 14-day free trial. No credit card. Cancel anytime.</p>
          <div className="lp-cta-actions">
            <button className="lp-btn-hero" onClick={enter}>Enter the App →</button>
            <button className="lp-btn-outline" onClick={() => scrollTo("features")}>See Features</button>
          </div>
          <div className="lp-cta-note">Starter from $29/mo · Pro $49/mo · No hidden fees · No tenant surcharges</div>
        </div>
      </div>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-grid">
          <div>
            <div className="lp-footer-brand">Rent<span>Lord</span></div>
            <div className="lp-footer-desc">Property management built for independent landlords and real estate investors in DFW and beyond. Built for landlords who think like investors.</div>
            <div className="lp-footer-trust">
              <div className="lp-trust-badge"><SvgIc n="shield" s={12} c="#22c55e"/> SSL Encrypted</div>
              <div className="lp-trust-badge"><SvgIc n="shield" s={12} c="#22c55e"/> SOC2 In Progress</div>
            </div>
          </div>
          <div className="lp-footer-col">
            <h4>Product</h4>
            <span onClick={() => scrollTo("features")}>Features</span>
            <span onClick={() => scrollTo("pricing")}>Pricing</span>
            <span onClick={() => scrollTo("compare")}>Compare</span>
            <span onClick={enter}>Sign Up</span>
            <span onClick={enter}>Sign In</span>
          </div>
          <div className="lp-footer-col">
            <h4>Company</h4>
            <span>About</span><span>Blog</span><span>Contact</span><span>Press Kit</span>
          </div>
          <div className="lp-footer-col">
            <h4>Legal</h4>
            <span style={{ cursor:'pointer' }} onClick={onShowTerms}>Terms of Service</span>
            <span style={{ cursor:'pointer' }} onClick={onShowPrivacy}>Privacy Policy</span>
            <span>Security</span>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <div>© 2026 RentLord. All rights reserved. · DFW, Texas</div>
          <div className="lp-footer-links">
            <span style={{ cursor:'pointer' }} onClick={onShowTerms}>Terms</span>
            <span style={{ cursor:'pointer' }} onClick={onShowPrivacy}>Privacy</span>
            <span>Contact</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
